import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./RolesCreate.css";
import privateApi from "../../apis/privateApi";
import ButtonSpinner from "../../components/ButtonSpinner/ButtonSpinner";
import Notification from "../../components/Notification/Notification";

const RolesCreate = () => {
    const navigate = useNavigate();

    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [permissions, setPermissions] = useState([]);
    const [allowedBudgetLevels, setAllowedBudgetLevels] = useState([]);
    const [level, setLevel] = useState(1);

    const [permissionsList, setPermissionsList] = useState([]);
    const [budgetLevels, setBudgetLevels] = useState([]);

    const [loading, setLoading] = useState(false);
    const [metaLoading, setMetaLoading] = useState(true);

    const [notification, setNotification] = useState({
        show: false,
        message: "",
        type: "info",
    });

    const handleCloseNotification = () => {
        setNotification({ ...notification, show: false });
    };

    const toggleSelection = (value, list, setList) => {
        if (list.includes(value)) {
            setList(list.filter((item) => item !== value));
        } else {
            setList([...list, value]);
        }
    };

    // 🔥 Format enums → "create_user" => "Create User"
    const formatLabel = (value) => {
        return value
            .replace(/_/g, " ")
            .replace(/\b\w/g, (char) => char.toUpperCase());
    };

    const fetchMeta = async () => {
        try {
            const res = await privateApi.get("/api/roles/meta");

            setPermissionsList(res.data.permissions || []);
            setBudgetLevels(res.data.allowedBudgetLevels || []);
        } catch (err) {
            console.error(err);
            setNotification({
                show: true,
                message: "Failed to load role metadata.",
                type: "error",
            });
        } finally {
            setMetaLoading(false);
        }
    };

    useEffect(() => {
        fetchMeta();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setNotification({ show: false, message: "", type: "info" });

        try {
            const res = await privateApi.post("/api/roles", {
                name,
                description,
                permissions,
                allowedBudgetLevels,
                level,
            });

            setNotification({
                show: true,
                message: res.data.message || "Role created successfully!",
                type: "success",
            });

            setTimeout(() => {
                navigate("/roles");
            }, 1000);
        } catch (err) {
            console.error(err);

            setNotification({
                show: true,
                message:
                    err?.response?.data?.message ||
                    "Failed to create role.",
                type: "error",
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="roles-create-container">
            <div className="roles-create-card">
                <h2>Create Role</h2>

                <Notification
                    show={notification.show}
                    message={notification.message}
                    type={notification.type}
                    onClose={handleCloseNotification}
                />

                <form onSubmit={handleSubmit} className="roles-create-form">
                    {/* Name */}
                    <label className="roles-create-form-label">Role Name</label>
                    <input
                        type="text"
                        placeholder="Enter role name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        required
                    />

                    {/* Description */}
                    <label className="roles-create-form-label">Description</label>
                    <textarea
                        placeholder="Enter description"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                    />

                    {/* Permissions */}
                    <label className="roles-create-form-label">Permissions</label>
                    {metaLoading ? <div className="roles-create-spinner-div">
                        <ButtonSpinner size={20} height={4} color="#008753" loadingInfo="Fetching permissions..." />
                    </div> :
                        <div className="roles-create-checkbox-group">
                            {permissionsList.map((perm) => (
                                <label key={perm} className="roles-create-checkbox-item">
                                    <input
                                        type="checkbox"
                                        checked={permissions.includes(perm)}
                                        onChange={() =>
                                            toggleSelection(perm, permissions, setPermissions)
                                        }
                                    />
                                    {formatLabel(perm)}
                                </label>
                            ))}
                        </div>
                    }

                    {/* Budget Levels */}
                    <label className="roles-create-form-label">Allowed Budget Levels</label>
                    {metaLoading ? <div className="roles-create-spinner-div">
                        <ButtonSpinner size={20} height={4} color="#008753" loadingInfo="Fetching Budget Levels..." />
                    </div> :

                        <div className="roles-create-checkbox-group">
                            {budgetLevels.map((lvl) => (
                                <label key={lvl} className="roles-create-checkbox-item">
                                    <input
                                        type="checkbox"
                                        checked={allowedBudgetLevels.includes(lvl)}
                                        onChange={() =>
                                            toggleSelection(
                                                lvl,
                                                allowedBudgetLevels,
                                                setAllowedBudgetLevels
                                            )
                                        }
                                    />
                                    {formatLabel(lvl)}
                                </label>
                            ))}
                        </div>
                    }

                    {/* Level */}
                    <label className="roles-create-form-label">Role Level</label>
                    <input
                        type="number"
                        min="1"
                        value={level}
                        onChange={(e) => setLevel(e.target.value)}
                    />
                    <div className="roles-create-action-btns">
                        <button type="submit" className="roles-create-btn" disabled={loading}>
                            {loading ? (
                                <ButtonSpinner size={10} textColor={"#fff"} backgroundColor="#fff" loadingInfo="Creating Role..." color="transparent" />
                            ) : (
                                "Create Role"
                            )}
                        </button>
                        {/* 🔙 Back Button */}
                        <button type="button" className="roles-create-back-btn" onClick={() => navigate(-1)}>Back</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default RolesCreate;